/**
 * Messaging provider contract for Request Correspondence (#1069).
 *
 * DRAFTED FROM THE PRD, NOT FROM ANY VENDOR SDK. That ordering is deliberate:
 * an interface shaped around one vendor's model makes that vendor look
 * effortless and every other option look awkward. See
 * `_bmad-output/.archive/1069-sourcing-and-evaluation/poc-test-design-1069.md`.
 *
 * Kept deliberately thin. Resist adding chat concepts (reactions, presence,
 * receipts, typing) that PRD r2 puts out of scope.
 *
 * IMPORTANT — what this interface does NOT do:
 * It does not decide who may see a space. Space membership is derived from
 * Vertiv roles on our side and is enforced by us. A provider is handed a space
 * and maps it to its own container; it is never the authority on visibility.
 * The server is, and it proves it: `correspondenceAuthorization.ts` hands the
 * storage layer a conversation record it has already checked.
 */

import type {
  Message,
  SpaceId,
  Thread,
  ThreadFileListing,
} from '../correspondence.types';

/**
 * The set of backends this contract has implementations for.
 *
 * Kept as a named type rather than inlined: the contract's value is the seam
 * between the panel and the message store, and the next entry here was expected
 * to be a first-party API rather than another vendor. `api` is that entry.
 *
 *   api    — our own `/api/v1`, Okta session, no vendor credential in the
 *            bundle. THE ONE THE APP USES.
 *   flarum — direct-to-vendor, holds a master key. Retained ONLY as the
 *            reference implementation the leak matrix runs against; it must
 *            never be selected in a deployed environment (NFR34).
 */
/**
 * ⚠️ `ProviderName` WAS RETIRED ON 2026-09-04 ALONG WITH THE ARM SELECTION IT
 * EXISTED FOR. It was a one-value union, `'api'`, and the machinery that read a
 * name from the environment and mapped it to an implementation went with it:
 * with a first-party engine there is no arm to choose (PRD r9, OQ1). `name`
 * below is now a plain label for diagnostics, not a key anything selects on.
 *
 * The history is worth one line, because it was a security fix rather than
 * tidiness: a `'flarum'` arm once shipped a browser-resident adapter holding a
 * master API key with no space authorization (NFR34), and deleting the name as
 * well as the module is what made the compiler prove it was gone.
 */

/*
 * ⚠️ `MessagingIdentity`, `connect` AND `disconnect` WERE REMOVED IN STORY
 * 1069-2.3 (D8). They modelled a session a first-party API does not have:
 * who is calling is decided from the Okta session on the server, and the
 * client never sends an identity. The flag they drove gated three fetch
 * effects in `useCorrespondence`; those now gate on `enabled`, `openThreadId`
 * and the viewer's id alone (`useCorrespondence.test.ts`, "load timing").
 */

/**
 * What a provider can actually do. The UI degrades honestly rather than
 * pretending — an unsupported capability is a POC finding, not a bug to hide.
 */
/**
 * A file on its way out (#1069 Story 4.1).
 *
 * ⚠️ NO SIZE FIELD, AND THAT IS DELIBERATE. Story 4.0 derives
 * `file_size_bytes` from the payload so a caller cannot misreport it; sending
 * one would put the number back in the client's gift. The staging strip's
 * `StagedFile` carries `sizeBytes` for the composer's own ceilings, and it is
 * dropped here.
 *
 * ⚠️ NO THUMBNAIL EITHER. The server regenerates it: the one thing the list
 * path renders for a file it will not otherwise disclose should not be
 * whatever shape the sender chose.
 */
/**
 * What a files call may observe about its upload (#1530 review, C1): the
 * progress events and the abort handle the transport already produces.
 * Optional on `createThread` and `editMessage`, whose callers have no pending
 * row today; `postMessageWithFiles` takes the same two by name.
 */
export interface UploadOptions {
  onProgress?: (percent: number | null) => void;
  /** Handed the abort handle BEFORE the first byte goes out. */
  onUploadStarted?: (abort: () => void) => void;
}

export interface OutgoingMessageFile {
  fileName: string;
  mimeType: string;
  /** Bare base64, no `data:` prefix. */
  fileData: string;
}

/**
 * What to ask for. Both fields are optional so an unpaged caller reads exactly
 * as it did before.
 */
export interface PageRequest {
  /**
   * How many items to return. Defaults to `DEFAULT_PAGE_LIMIT`.
   *
   * A non-positive or fractional limit is REFUSED before a request is issued.
   * Flarum answers `page[limit]=-1` with HTTP 200, the whole collection and a
   * `next` link pointing at itself, so a caller following the cursor would spin
   * forever against a live backend. A provider above the cap clamps rather than
   * failing, and the short page it returns is authoritative.
   */
  limit?: number;
  /**
   * OPAQUE. Hand back exactly what the provider gave you; never construct,
   * decode, mutate or persist-and-reinterpret one.
   *
   * ⚠️ IT IS NOT A POSITION, AND MUST NEVER BECOME ONE. An offset (or a
   * base36/base64/hashed offset, which is the same thing wearing a coat) is
   * invalidated by any write: with newest-first ordering, one message posted
   * mid-scroll shifts every older message down a slot, so the next page
   * re-serves the boundary message, and one deletion skips a message. A cursor
   * names a stable ITEM IDENTITY, which is why `Page` can promise that older
   * pages stay stable while people are still writing.
   */
  cursor?: string;
}

/**
 * One page of a newest-first collection.
 *
 * `nextCursor` is the ONLY exhaustion signal: an empty `items` does not mean
 * the end has been reached, because a page can be emptied by items the caller
 * is not entitled to see or that the provider filters out.
 */
export interface Page<T> {
  items: T[];
  /** Absent when nothing older remains. */
  nextCursor?: string;
}

/** What a provider returns when the caller asks for no particular size. */
export const DEFAULT_PAGE_LIMIT = 20;

/**
 * The largest page any provider will serve.
 *
 * Flarum SILENTLY clamps to 50 and still answers 200, so an adapter that asked
 * for 100 and trusted the count would read a 50-item response as if it were
 * 100 and lose the difference without an error anywhere.
 */
export const MAX_PAGE_LIMIT = 50;

/**
 * The most files one conversation's listing will carry (#1069 Story 7.4).
 *
 * A CEILING, NOT A PAGE SIZE: the listing has no cursor. Every image row
 * carries an inline thumbnail (~8-15 KB of base64; a transparent source is a
 * palette PNG capped at 24 KB binary, ~32 KB of base64, else a white-flattened
 * JPEG, so 500 such rows are ~16 MB), and six files a message
 * over two hundred messages would be 1,200 of them out of one statement, so
 * the server binds `TOP` and sets `truncated` when it had more. The count
 * reads `Files (500+)` in that case.
 *
 * Mirrors `server/services/correspondence.types.ts`; both copies are pinned by
 * `server/services/__tests__/correspondence.types.test.ts`.
 */
export const MAX_THREAD_FILE_LISTING = 500;

/**
 * The longest conversation title the server accepts (Story 1069-1.3). The
 * composer's title input carries it as `maxLength`. Mirrors
 * `server/services/correspondence.types.ts`; both copies are pinned by
 * `server/services/__tests__/correspondence.types.test.ts`.
 */
export const MAX_TITLE_LENGTH = 255;

/**
 * The longest message the server accepts, in characters of plain text. The
 * composer disables Send above it so the refusal is rare; the server measures
 * the cleaned body's text projection and is the control. Mirrors the server
 * copy; pinned on both tiers.
 */
export const MAX_MESSAGE_LENGTH = 10_000;

/**
 * The ceiling on a message's cleaned MARKUP, mirrored from the server's
 * `MAX_MESSAGE_HTML_LENGTH` (#1530; `correspondenceSanitizer.ts`). The text
 * limit alone admits a 200,000-character `href` around two characters of
 * text; this bounds the row. The composer refuses above it with the server's
 * own sentence so nobody uploads a message to be told no.
 */
export const MAX_MESSAGE_HTML_LENGTH = 50_000;

export interface IMessagingProvider {
  /** A label for diagnostics. Nothing selects on it. */
  readonly name: string;

  /**
   * The request's conversations in ONE audience, newest activity first, as a
   * plain array (Story 1069-1.2, AC 4). Unpaged, unlike `listMessages`: a
   * request's conversation count is small by construction and the server
   * answers from its own index in one query (`correspondenceService.listThreads`),
   * so a cursor here would be a promise with nothing behind it. An audience the
   * caller may not see comes back empty, indistinguishable from an empty one;
   * a response that is not a page at all is an error, never an empty list.
   */
  listThreads(requestId: string, space: SpaceId): Promise<Thread[]>;

  /**
   * Start a conversation WITH ITS FIRST MESSAGE (Story 1069-1.3, D1). The
   * first message is required and travels with the title: a conversation with
   * nothing in it has nothing to read, nothing to preview and nothing to be
   * unread about, so the server creates both rows in one transaction and
   * refuses an empty body. The audience is `space`, validated server-side.
   * Resolves to the created `Thread` (the first message is loaded by the
   * ordinary message fetch when the panel opens it).
   */
  createThread(
    requestId: string,
    space: SpaceId,
    title: string,
    body: string,
    /**
     * Files on the FIRST message (#1530): inline images the editor staged,
     * sent in the same body as the conversation (C3). Absent or empty is the
     * text-only call every caller before #1530 made.
     */
    files?: OutgoingMessageFile[],
    /** Progress and abort for a files create; ignored without files. */
    options?: UploadOptions,
  ): Promise<Thread>;

  /**
   * ⚠️ `addMembers` WAS REMOVED FROM THIS CONTRACT IN #1069 P1.1.
   *
   * It let the browser author thread membership, which is how an internal caller
   * could make an external person a permanent recipient of an internal thread.
   * Membership is now a projection of the request team, computed server-side and
   * reconciled in both directions on every listing, so there is nothing for a
   * provider to be asked to do.
   *
   * Leak case L9 (backscroll on join) is unchanged as a question and is now
   * answered in one place: a member added to the team sees the history, because
   * the reconcile puts them on the existing threads.
   */

  /**
   * Newest first, so a first call is the bottom of the conversation and each
   * cursor walks BACKWARDS through history (FR6). The panel reverses for
   * display, because FR66 puts the newest message at the bottom.
   */
  listMessages(threadId: string, page?: PageRequest): Promise<Page<Message>>;
  postMessage(threadId: string, body: string): Promise<Message>;

  /**
   * Post a message WITH files (#1069 Story 4.1, AC 5, 9, 11).
   *
   * ⚠️ A SEPARATE METHOD RATHER THAN OPTIONAL ARGUMENTS ON `postMessage`, and
   * the reason is the return type, not the inputs. This one hands back an
   * ABORT as well as a promise (AC 11 / FR18) and reports progress as it goes
   * (AC 9), neither of which `postMessage`'s `Promise<Message>` can express.
   * Bolting them on would make every existing caller carry a handle it has no
   * use for and must remember not to leak.
   *
   * ⚠️ IT DOES NOT GO THROUGH `transport.request`. That seam is `fetch`-shaped
   * and can neither report upload progress on native nor abort mid-body (C2).
   * See `src/services/correspondence/postWithProgress.ts`.
   *
   * Optional on the interface so the in-memory provider, and any future one
   * that has no upload story, remain valid without a stub that lies.
   */
  postMessageWithFiles?(args: {
    threadId: string;
    body: string;
    files: OutgoingMessageFile[];
    onProgress?: (percent: number | null) => void;
  }): { promise: Promise<Message>; abort: () => void };

  /**
   * Record that the caller has been shown this thread (#1069 P3.1, FR56).
   *
   * ⚠️ IT FIRES ON THREAD OPEN, NEVER ON A SCROLL POSITION. FR56 is "opening
   * clears it", not "reaching the bottom clears it", and a scroll-driven
   * version would need the panel to know how far down the list a message is,
   * which is presentation state the provider must not depend on.
   *
   * FIRE AND FORGET at every call site. Read state is an affordance: a banner
   * over a thread the user is currently reading is worse than a stale badge, so
   * a failure here must never reach `state.error`.
   *
   * There is no unread COUNT in this contract and there must not be one. The
   * product owner withdrew every count on 2026-08-25 (PRD r8, FR54/FR55): the
   * signal is a dot, present or absent. A `capabilities.unreadCounts` flag used
   * to sit alongside it, declaring a backend feature this product never
   * rendered; it went with the rest of the capability block on 2026-09-16.
   */
  markThreadRead(threadId: string): Promise<void>;

  /**
   * Correct your OWN message (Story 1069-1.3). Author only: the server
   * answers 403 with an explanation for anybody else, an IT Administrator
   * included. The body is overwritten (no revision history) and the returned
   * `Message` carries `editedAt`; nobody's unread state changes.
   */
  editMessage(
    threadId: string,
    messageId: string,
    body: string,
    /**
     * Files to ADD to the message (#1530): inline images inserted during the
     * edit. The server judges the ceilings over the files the message already
     * holds plus these; the returned `Message` carries every live file.
     */
    files?: OutgoingMessageFile[],
    /** Progress and abort for a files edit; ignored without files. */
    options?: UploadOptions,
  ): Promise<Message>;

  /**
   * Withdraw a message (Story 1069-1.3): your own, or anybody's when you are
   * an IT Administrator. It stays in place as a tombstone ("Message
   * withdrawn"); the text is never returned again. Withdrawing a tombstone
   * is a 404.
   */
  deleteMessage(threadId: string, messageId: string): Promise<void>;

  /**
   * Rename the conversation (Story 1069-7.3, FR74).
   *
   * ⚠️ ANYONE WHO MAY POST MAY RENAME (D1). A title is shared property, not
   * its starter's; the author-only rule of `editMessage` does not transfer,
   * because a message has an author and a conversation has participants. A
   * caller who may read but not write is refused with the gate's explanation.
   *
   * ⚠️ A RENAME IS NOT ACTIVITY (D2). No timestamp moves, the listing does not
   * reorder and nobody's dot changes. There is no title history.
   *
   * Resolves with the renamed conversation in the LISTING shape.
   */
  renameThread(threadId: string, title: string): Promise<Thread>;

  /**
   * Every file of every live message in the conversation, newest message
   * first and upload order within a message (#1069 Story 7.4, FR77).
   *
   * ⚠️ ONE CALL, NO CURSOR, AND THE COUNT IS THE LIST'S OWN (owner,
   * 2026-09-17). There is deliberately no head request and no count endpoint:
   * a control reading "Files (n)" cannot know `n` without the list, and a
   * second wire shape is a second thing to keep invalidated.
   *
   * Bounded by `MAX_THREAD_FILE_LISTING`; `truncated` says the server had
   * more.
   */
  listThreadFiles(threadId: string): Promise<ThreadFileListing>;
}
